import { Drawer } from 'antd';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { setCards, setCurrentCard } from 'shared/config/store/reducers/cardsSlice';
import { useOrderMutation } from '../api';
import { Step } from '../model/types';
import Info from './Info';
import Pin from './Pin';
import Success from './Success';
import { Account } from 'entities/account/model/types';
import { ApiError } from 'shared/model/types';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'shared/const/routes';

type GetPlasticProps = {
  open: boolean;
  onClose: () => unknown;
};

const DRAWER_WIDTH = 560;

const GetPlastic: React.FC<GetPlasticProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>(Step.Info);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const { showError, showSuccess } = useMessage();

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const userMeta = useSelector(getUserMeta);

  const [order] = useOrderMutation();

  const handleClose = () => {
    setStep(Step.Info);
    onClose();
  };

  const onSubmit = async (data: { '3ds': string }) => {
    if (!currentAccount) return;

    dispatch(setActivity(true));
    await order({
      accountId: String(currentAccount.id),
      staticPassword: data['3ds'],
      address1: userMeta.address.line1,
      address2: userMeta.address.line2 || '',
      postalCode: userMeta.address.postalCode,
      city: userMeta.address.city,
      phoneCountry: userMeta.phoneCountry,
    })
      .unwrap()
      .then((response) => {
        showSuccess(t({ id: 'card.getPlastic.messages.success.title' }));
        dispatch(
          setCards({
            accountId: currentAccount.id,
            cards: [response.response],
            emi: currentEmi
          })
        );
        dispatch(setCurrentCard({ emi: currentEmi, cardId: response.response.id }));
        setStep(Step.Success);
      })
      .catch((err: ApiError) => {
        const error = err.data?.errors?.[0];
        const errorText = error?.defaultMessage || t({ id: 'card.getPlastic.messages.error.subtitle' });
        showError(errorText);
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const handleSuccessDone = () => {
    handleClose();
    navigate(ROUTES.CARD.path);
  };

  const renderContent = () => {
    switch (step) {
      case Step.Info:
        return <Info onSubmit={() => setStep(Step.Pin)} />;
      case Step.Pin:
        return <Pin onSubmit={onSubmit} />;
      case Step.Success:
        return <Success onSubmit={handleSuccessDone} />;
      default:
        return <Info onSubmit={() => setStep(Step.Pin)} />;
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width={DRAWER_WIDTH}
      destroyOnClose
      bodyStyle={{ padding: 24, display: 'flex', flexDirection: 'column' }}
    >
      {renderContent()}
    </Drawer>
  );
};

export default GetPlastic;
